<template>
    <span v-if="showButtons" class="d-flex align-center justify-end">
        <app-tooltip
            v-if="enableXlsx && (!only || only.includes('xlsx'))"
            :value="$t('download_as_xlsx')"
            :disabled="disabled"
        >
            <app-button
                icon="spreadsheet"
                :loading="loading === 'xlsx'"
                :disabled="!!loading || disabled"
                @click="downloadAsXlsx()"
            />
        </app-tooltip>
        <app-tooltip
            v-if="enablePdf && (!only || only.includes('pdf'))"
            :value="$t('download_as_pdf')"
            :disabled="disabled"
        >
            <app-button
                icon="pdf"
                :loading="loading === 'pdf'"
                :disabled="!!loading || disabled"
                @click="downloadAsPdf()"
            />
        </app-tooltip>
    </span>
</template>

<script>
import exportMixin from '@/mixins/export';
import { BaseModel } from '@/models';

export default {
    name: 'AppExportTable',
    mixins: [exportMixin],
    props: {
        name: {
            type: String,
            default: null
        },
        only: {
            type: Array,
            default: null
        },
        headers: {
            type: Array,
            default: () => []
        },
        defaultHeaders: {
            type: Array,
            default: () => []
        },
        items: {
            type: Array,
            default: () => []
        },
        requestItems: {
            type: Function,
            default: null
        },
        filters: {
            type: Object,
            default: () => {}
        },
        excludeHeaders: {
            type: Array,
            default: () => ['id', 'control']
        },
        onExportXlsx: {
            type: Function,
            default: null
        },
        onExportPdf: {
            type: Function,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        cast: {
            type: Function,
            default: null
        }
    },
    data: () => ({
        loading: null,
        enableXlsx: true,
        enablePdf: true
    }),
    computed: {
        showButtons() {
            if (this.headers.length && (this.items.length || this.requestItems)) {
                return true;
            }

            if (this.onExportXlsx || this.onExportPdf) {
                return true;
            }

            return false;
        },
        includedHeaders() {
            const headers = this.headers.filter(header => {
                if (!this.defaultHeaders || !this.defaultHeaders.length) {
                    return true;
                }

                return this.defaultHeaders.includes(header.value);
            });
            return this.getHeaders(headers, this.excludeHeaders);
        }
    },
    methods: {
        async downloadAsXlsx() {
            this.loading = 'xlsx';
            this.setAppLoading(true, this.$t('exporting_as_xlsx'));
            await this.wait(1400);

            try {
                if (this.onExportXlsx) {
                    await this.onExportXlsx();
                } else {
                    const rows = await this.getRows();
                    const headers = [...this.includedHeaders];
                    const json = this.convertRowsToJson(rows, [...headers]);
                    const headerStrings = headers.map(header => header.text);

                    this.toXlsx(headerStrings, json, this.name);
                }
            } catch (error) {
                this.handleError(error);
            }

            this.setAppLoading(false);
            this.loading = null;
        },
        async downloadAsPdf() {
            this.loading = 'pdf';
            this.setAppLoading(true, this.$t('exporting_as_pdf'));
            await this.wait(1400);

            try {
                if (this.onExportPdf) {
                    await this.onExportPdf();
                } else {
                    const rows = await this.getRows();
                    const head = this.convertHeadersToHead([...this.includedHeaders]);
                    const body = this.convertRowsToBody(rows, head);
                    const columns = head.map(cell => ({
                        header: cell.content,
                        dataKey: cell.header.value
                    }));

                    await this.toTable(head, columns, body, this.name);
                }
            } catch (error) {
                this.handleError(error);
            }

            this.setAppLoading(false);
            this.loading = null;
        },
        //
        async getRows() {
            let rows = [];

            if (this.items.length > 0) {
                rows = this.items;
            } else if (this.requestItems) {
                rows = await this.collectItems(this.requestItems, {
                    filters: this.filters
                });
            }

            if (this.cast) {
                return rows.map(r => (r instanceof BaseModel ? r : new this.cast(r)));
            }

            return rows;
        }
    }
};
</script>
